import PropTypes from 'prop-types'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { MediaContext } from '../../context/MediaContext'
import useIntersect from '../hooks/useIntersect'
import { measureTotalLineLength } from '../utils/measureTotaLineLength'
import './styles.scss'

const StageCanvas = ({ lines, dashValue }) => {
  const { viewportWidth, breakpoint } = useContext(MediaContext)

  const canvasRef = useRef(null)
  const raf = useRef(null)

  const intersectionEntry = useIntersect(canvasRef)

  const [width, setWidth] = useState(null)
  const [height, setHeight] = useState(null)
  const [scaleFactor, setScaleFactor] = useState()
  const [pixelRatio, setPixelRatio] = useState(null)
  const [ctx, setCtx] = useState(null)
  const [drawMaterial, setDrawMaterial] = useState()
  const [canvasReady, setCanvasReady] = useState(false)
  const [contextReady, setContextReady] = useState(false)

  const speed = 0.3
  const baseSize = {
    desktop: [1000, 640],
    mobile: [400, 800]
  }

  let progress = 0
  let fastProgress = 0

  const drawLines = () => {
    progress = progress + speed
    fastProgress = fastProgress + speed * 2
    ctx.clearRect(0, 0, width, height)

    drawMaterial.forEach(line => {
      const progressValue = line.fast ? fastProgress : progress
      ctx.beginPath()
      line.coordinates.forEach((points, index) => {
        index === 0 ? ctx.moveTo(points.x, points.y) : ctx.lineTo(points.x, points.y)
      })

      if (line.animate) {
        ctx.setLineDash([line.length * dashValue, line.length * dashValue])
        ctx.lineDashOffset = line.reverse ? -line.length + progressValue : line.length - progressValue
      } else {
        ctx.setLineDash([1, 0])
        ctx.lineDashOffset = 0
      }
      ctx.strokeStyle = line.color
      ctx.stroke()
    })

    raf.current = requestAnimationFrame(drawLines)
  }

  useEffect(() => {
    if (viewportWidth) {
      const [desktopBaseWidth] = baseSize.desktop
      const [mobileBaseWidth] = baseSize.mobile
      const newScaleFactor = breakpoint.md ? viewportWidth / desktopBaseWidth : viewportWidth / mobileBaseWidth
      setScaleFactor(newScaleFactor)
      setPixelRatio(window.devicePixelRatio)
      setWidth(viewportWidth)
      breakpoint.md ? setHeight(Math.round(viewportWidth * 0.64)) : setHeight(viewportWidth * 2)
    }
  }, [viewportWidth])

  useEffect(() => {
    if (width && height) {
      const newDrawMaterial = lines.map(line => {
        const mediaCoordinates = breakpoint.md ? line.coordinates.horizontal : line.coordinates.vertical
        const length = measureTotalLineLength(mediaCoordinates)

        return {
          ...line,
          coordinates: mediaCoordinates,
          length
        }
      })

      if (newDrawMaterial.length === 0) {
        return
      }

      setDrawMaterial(newDrawMaterial)
      setCanvasReady(true)
    }
  }, [width, height])

  useEffect(() => {
    if (canvasReady) {
      canvasRef.current.style.width = width + 'px'
      canvasRef.current.style.height = height + 'px'

      setCtx(canvasRef.current.getContext('2d', { alpha: true }))
    }
  }, [width, height, canvasReady])

  useEffect(() => {
    if (ctx && drawMaterial) {
      const retinaScale = scaleFactor * pixelRatio

      ctx.scale(retinaScale, retinaScale)
      ctx.lineWidth = Math.round((1.5 / scaleFactor) * 10) / 10

      setContextReady(true)
    }

    if (contextReady) {
      cancelAnimationFrame(raf.current)
      requestAnimationFrame(drawLines)
    }
  }, [ctx, drawMaterial])

  useEffect(() => {
    if (contextReady) {
      if (intersectionEntry.isIntersecting) {
        raf.current = requestAnimationFrame(drawLines)
      } else {
        cancelAnimationFrame(raf.current)
      }
    }

    return () => cancelAnimationFrame(raf.current)
  }, [intersectionEntry.isIntersecting])

  return (
    <div className="stage-canvas__container">
      <canvas className="stage-canvas" ref={canvasRef} width={width * pixelRatio} height={height * pixelRatio} />
    </div>
  )
}

StageCanvas.propTypes = {
  size: PropTypes.number,
  lines: PropTypes.array,
  dashValue: PropTypes.number
}

StageCanvas.defaultProps = {
  dashValue: 0.5
}

export default StageCanvas
